import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectRooms, selectSelectedRoom, selectSelectedRoomId } from "../../../store/room/room-selectors";
import { computeAbsolutePositions, listedRoomCardWidthPx } from "../../../utils/floors-graph-helper";
import { FloorStation } from "../../../interfaces/robot/rooms/station";
import { useTheme } from "@mui/material";
import ListedRoom from "./ListedRoom";
import { selectIsWebApp } from "../../../store/app-configurations/app-configurations-selectors";
import { FilterKeys } from "../../../enums/query-params/filters";
import SphereRobotTabs from "../../../enums/query-params/sphere-robot-tabs";
import WebAppNavigationRoutes from "../../../enums/query-params/webapp-navigation-routes";
import MapQueryParams, { MapTypeParam } from "../../../enums/robot/mapping/mapping-query";
import { selectCurrentRobotState } from "../../../store/status/status-selectors";
import RswState from "../../../enums/robot/rsw/rsw-state";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import { RoomMiniOption } from "../RoomSelectors/RoomSelect";
import ConfirmAbortDialog from "../../General/ConfirmAbortDialog";

const FloorPlanner: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setQueryParams] = useSearchParams();
  const dispatch = useSharedDispatch();
  const navigate = useNavigate();

  const { patchRoomById } = useSharedActions();

  const availableRooms = useSharedSelector((state) => selectRooms(state));
  const isWebapp = useSharedSelector(state => selectIsWebApp(state));
  const currentRobotState = useSharedSelector((state) => selectCurrentRobotState(state));
  const roomData = useSharedSelector((state) => selectSelectedRoom(state));
  const currentRoomId = useSharedSelector((state) => selectSelectedRoomId(state));

  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [confirmImportRoomId, setConfirmImportRoomId] = useState<string | null>(null);
  const [hoveredStationIds, setHoveredStationIds] = useState<string[]>([]);

  const rooms = useMemo(() => {
    const rooms = availableRooms.filter(
      (r) => r.floorId === availableRooms.find((ra) => ra.id === currentRoomId)?.floorId && !r.isArchived
    );
    if (rooms.length > 0) return rooms;
    else {
      return availableRooms.filter(
        (r) => r.floorId === availableRooms.find((ra) => ra.id === currentRoomId)?.floorId && r.isArchived
      );
    }
  }, [availableRooms, currentRoomId]);

  const roomClickHandler = (roomId: string, isFloorRoom?: boolean) => {
    if (isFloorRoom === true && selectedRoomId !== roomId) {
      setSelectedRoomId(roomId);
    } else {
      if (isWebapp) {
        navigate(`${WebAppNavigationRoutes.routeRoomPlanner}?${FilterKeys.RoomId}=${roomId}`);
      } else {
        setQueryParams(prevState => {
          prevState.set(FilterKeys.Tab, SphereRobotTabs.MapPlanner)
          prevState.set(FilterKeys.RoomId, roomId)
          return prevState;
        })
      }
    }
  };

  const newMappingClickHandler = (stationId: string) => {
    if (roomData != null) {
      navigate({
        pathname: `${WebAppNavigationRoutes.routeRoomMapping}/`,
        search: `?${FilterKeys.FloorId}=${roomData.floorId}&${MapQueryParams.PrimaryStation}=${stationId}&${MapQueryParams.MappingType}=${MapTypeParam.NewMapping}`,
      });
    }
  };

  const clickAwayHandler = () => {
    setSelectedRoomId(null);
  };

  const openConfirmImportRoomClickHandler = (roomId: string) => {
    setConfirmImportRoomId(roomId);
  };

  const closeConfirmImportRoomHandler = () => {
    setConfirmImportRoomId(null);
  };

  const importRoomClickHandler = () => {
    if (confirmImportRoomId != null && roomData?.floorId != null) {
      dispatch(
        patchRoomById({ id: confirmImportRoomId, data: { floorId: roomData.floorId, id: confirmImportRoomId } }),
      );
      setConfirmImportRoomId(null);
    }
  };

  const {
    stations: absoluteStationPositions,
    coordinateX: minWidth,
    coordinateY: minHeight,
  } = rooms.length === 1
    ? {
        coordinateX: rooms[0].primaryStation.coordinateX,
        coordinateY: rooms[0].primaryStation.coordinateY,
        stations: [{ ...rooms[0].primaryStation, isTransition: false, id: rooms[0].primaryStation.stationId } as FloorStation],
      }
    : computeAbsolutePositions(rooms.filter((r) => r.secondaryStation != null));

  const isRobotInIdleState = useMemo(
    () => currentRobotState?.status?.state === RswState.Idle || currentRobotState?.status?.state === RswState.Ready,
    [currentRobotState?.status?.state],
  );

  const mappingDisabledTooltip = isRobotInIdleState
    ? undefined
    : currentRobotState == null
      ? t("mapping-robot-unknown-state-error")
      : t("mapping-robot-not-idle-warning");

  return (
    <React.Fragment>
      <Box sx={{ position: "relative", height: "100%" }}>
        {absoluteStationPositions.map((station) => {
          const room = rooms.find(
            (r) =>
              (r.primaryStation.stationId === station.id && r.secondaryStation == null) ||
              `${r.primaryStation.stationId},${r.secondaryStation?.stationId}` === station.id,
          );
          const possibleRoom = availableRooms.find(
            (r) => r.primaryStation.stationId === station.id && r.floorId !== roomData?.floorId && !r.isArchived,
          );

          return (
            <Box
              style={{ paddingTop: 0 }}
              key={station.id}
              onMouseOver={
                room != null
                  ? () => setHoveredStationIds([room.primaryStation.stationId, room.secondaryStation?.stationId ?? ""])
                  : undefined
              }
              onMouseLeave={() => setHoveredStationIds([])}
              sx={(theme) => ({
                position: "absolute",
                left: `calc(${station.coordinateX}px + ${theme.spacing(0.2)})`,
                top: `calc(${station.coordinateY}px + ${theme.spacing(0.2)})`,
                width: listedRoomCardWidthPx,
                height: listedRoomCardWidthPx,
                borderRadius: 1,
                boxShadow: 2,
                zIndex: room?.id === selectedRoomId ? 2 : 1,
                backgroundColor: theme.palette.background.default,
              })}
            >
              {room == null ? (
                <Box p={2} px={1}>
                  <Typography variant="body2" fontWeight={500} my={1} textAlign="center">
                    {t("empty-room")}
                  </Typography>
                  <Divider />
                  {possibleRoom ? (
                    <ReadOnlyProtectedButton
                      sx={{ justifyContent: "flex-start" }}
                      fullWidth
                      onClick={() => openConfirmImportRoomClickHandler(possibleRoom.id)}
                    >
                      <RoomMiniOption roomMetaInfo={possibleRoom} />
                    </ReadOnlyProtectedButton>
                  ) : (
                    <Tooltip title={mappingDisabledTooltip}>
                      <span>
                        <ReadOnlyProtectedButton
                          sx={{ justifyContent: "flex-start" }}
                          disabled={!isRobotInIdleState}
                          fullWidth
                          onClick={() => newMappingClickHandler(station.id)}
                        >
                          {t("new-room-mapping")}
                        </ReadOnlyProtectedButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={t("transition-mapping-not-on-empty-room")} placement="right">
                    <span>
                      <Button sx={{ justifyContent: "flex-start" }} fullWidth disabled>
                        {t("roommappingwizard-transition-mapping")}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              ) : (
                <ListedRoom
                  key={room.id}
                  mapRotation={station.coordinateTheta}
                  isFloorRoom
                  isSelected={selectedRoomId === room.id}
                  name={room.name.length === 0 ? t("unknown-room-name") : room.name}
                  roomMetaInfo={room}
                  onClick={() => {
                    roomClickHandler(room.id, true);
                  }}
                  onClickAway={clickAwayHandler}
                  showTransitionMappingButton
                  allowDelete={rooms.length === 1 || room.secondaryStation != null}
                />
              )}
            </Box>
          );
        })}
        <svg width={minWidth + listedRoomCardWidthPx} height={minHeight + listedRoomCardWidthPx}>
          {absoluteStationPositions.map((station, index) => {
            const transition = rooms.find(
              (r) =>
                (r.primaryStation.stationId === station.id && r.secondaryStation != null) ||
                r.secondaryStation?.stationId === station.id,
            );
            const stationA = absoluteStationPositions.find((s) => s.id === transition?.primaryStation.stationId);
            const stationB = absoluteStationPositions.find((s) => s.id === transition?.secondaryStation?.stationId);
            const middleAbsolute = absoluteStationPositions.find(
              (s) => `${transition?.primaryStation?.stationId},${transition?.secondaryStation?.stationId}` === s.id,
            );
            if (stationA != null && stationB != null && middleAbsolute != null) {
              return (
                <React.Fragment>
                  <line
                    key={`line-${index}-A`}
                    x1={stationA.coordinateX + listedRoomCardWidthPx / 2}
                    y1={stationA.coordinateY + listedRoomCardWidthPx / 2}
                    x2={middleAbsolute.coordinateX + listedRoomCardWidthPx / 2}
                    y2={middleAbsolute.coordinateY + listedRoomCardWidthPx / 2}
                    stroke={theme.palette.primary.main}
                    strokeWidth={16}
                  />
                  <line
                    key={`line-${index}-B`}
                    x1={stationA.coordinateX + listedRoomCardWidthPx / 2}
                    y1={stationA.coordinateY + listedRoomCardWidthPx / 2}
                    x2={middleAbsolute.coordinateX + listedRoomCardWidthPx / 2}
                    y2={middleAbsolute.coordinateY + listedRoomCardWidthPx / 2}
                    stroke={
                      hoveredStationIds.includes(stationA.id) && stationA.id === hoveredStationIds[0]
                        ? theme.palette.primary.dark
                        : theme.palette.primary.light
                    }
                    strokeWidth={10}
                  />
                  <line
                    key={`line-${index}-C`}
                    x1={middleAbsolute.coordinateX + listedRoomCardWidthPx / 2}
                    y1={middleAbsolute.coordinateY + listedRoomCardWidthPx / 2}
                    x2={stationB.coordinateX + listedRoomCardWidthPx / 2}
                    y2={stationB.coordinateY + listedRoomCardWidthPx / 2}
                    stroke={theme.palette.primary.main}
                    strokeWidth={16}
                  />
                  <line
                    key={`line-${index}-D`}
                    x1={middleAbsolute.coordinateX + listedRoomCardWidthPx / 2}
                    y1={middleAbsolute.coordinateY + listedRoomCardWidthPx / 2}
                    x2={stationB.coordinateX + listedRoomCardWidthPx / 2}
                    y2={stationB.coordinateY + listedRoomCardWidthPx / 2}
                    stroke={
                      hoveredStationIds.includes(stationB.id) &&
                      (stationB.id === hoveredStationIds[1] || hoveredStationIds[1] === "")
                        ? theme.palette.primary.dark
                        : theme.palette.primary.light
                    }
                    strokeWidth={10}
                  />
                </React.Fragment>
              );
            }
            return null;
          })}
        </svg>
      </Box>
      <ConfirmAbortDialog
        isOpen={confirmImportRoomId != null}
        title={t("confirm-import-room")}
        description={t("import-room-warning")}
        abortButtonText={t("button-abort")}
        confirmButtonText={t("button-confirm")}
        onConfirm={importRoomClickHandler}
        onAbort={closeConfirmImportRoomHandler}
      />
    </React.Fragment>
  );
};

export default FloorPlanner;
